export enum UnderwritingPolicyAttachmentStatus {
  ACTIVE = "ACTIVE",
  ACTIVE_EXPERIMENT = "ACTIVE_EXPERIMENT",
  SHADOW = "SHADOW",
  INACTIVE = "INACTIVE",
}

export enum ApplicabilityStrategy {
  USER_ID_SUFFIX = "USER_ID_SUFFIX",
  SEGMENT = "SEGMENT",
  GEOLOCATION = "GEOLOCATION",
}

export interface GeoApplicabilityConfig {
  circle?: {
    lat: number;
    lng: number;
    radiusMetres: number;
  };
  boundingBox?: {
    minLat: number;
    maxLat: number;
    minLng: number;
    maxLng: number;
  };
  maxStaleDurationHours: number;
}

export interface PolicyDiffSummary {
  total_added: number;
  total_removed: number;
  total_modified: number;
  sections_changed: string[];
}

export interface PolicyDiffBusinessSummary {
  effect: string;
  key_changes: string[];
}

export interface PolicyDiffResult {
  base_policy: string;
  target_policy: string;
  base_hash: string;
  target_hash: string;
  has_changes: boolean;
  summary: PolicyDiffSummary;
  business_summary: PolicyDiffBusinessSummary;
}

export interface PolicyDiffMapping {
  newPolicy: string;
  oldPolicy: string;
  diff?: PolicyDiffResult | null;
}

export interface TimelinePolicyDiffMapping {
  newPolicy: string;
  oldPolicy: string;
  diff?: { business_summary: PolicyDiffBusinessSummary } | null;
}

export interface UnderwritingPolicyAttachment {
  _id: string;
  employerId: string;
  policies: string[];
  status: UnderwritingPolicyAttachmentStatus;
  description?: string;
  applicabilityMetadata?: {
    strategy: ApplicabilityStrategy;
    userIdSuffixes: string[];
    segmentId?: string;
    geoConfig?: GeoApplicabilityConfig;
  };
  experimentName?: string;
  priority?: number;
  createdBy?: string;
  actorEmailId?: string;
  statusLogs?: {
    status: UnderwritingPolicyAttachmentStatus;
    actorEmailId: string;
    createdAt: string;
  }[];
  createdAt: string;
  updatedAt?: string;
  policyDiffMappings?: PolicyDiffMapping[];
}

export interface PolicyAttachmentFilterOptions {
  employerIds: string[];
  policies: string[];
  experimentNames: string[];
  statuses: UnderwritingPolicyAttachmentStatus[];
}

export interface CreatePolicyAttachmentRequest {
  employerId: string;
  policies: string[];
  description?: string;
  applicabilityMetadata?: {
    strategy: ApplicabilityStrategy;
    userIdSuffixes: string[];
    segmentId?: string;
    geoConfig?: GeoApplicabilityConfig;
  };
  policyDiffMappings?: { newPolicy: string; oldPolicy: string }[];
}

export interface UpdatePolicyAttachmentStatusRequest {
  toStatus: UnderwritingPolicyAttachmentStatus;
  employerId: string;
  remarks?: string;
  experimentName?: string;
  priority?: number;
}

export interface UpdatePolicyAttachmentStatusResponse {
  updatedPolicy: UnderwritingPolicyAttachment;
  demotedPolicy?: UnderwritingPolicyAttachment;
}

export interface PolicyAttachmentCreationOptions {
  employers: Array<{ _id: string; name: string }>;
  availablePolicies: Array<{ name: string; description: string }>;
}

export interface PolicyAttachmentFilters {
  employerIds?: string[];
  policies?: string[];
  attachmentId?: string;
  statusList?: UnderwritingPolicyAttachmentStatus[];
  experimentNames?: string[];
  limit?: number;
}

export interface PolicyAttachmentTimelineEvent {
  attachmentId: string;
  policies: string[];
  status: UnderwritingPolicyAttachmentStatus;
  timestamp: string;
  actorId: string;
  actorEmailId?: string;
  remarks?: string;
  experimentName?: string;
  applicabilityMetadata?: {
    strategy: ApplicabilityStrategy;
    userIdSuffixes: string[];
    segmentId?: string;
    geoConfig?: GeoApplicabilityConfig;
  };
  description?: string;
  policyDiffMappings?: TimelinePolicyDiffMapping[];
}

export interface PolicyAttachmentTimeline {
  employerId: string;
  events: PolicyAttachmentTimelineEvent[];
}
